import { useState, useEffect } from 'react';
import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate, useParams } from "react-router";
import * as theme from '../../theme';
import Heading from '../../components/Heading';
import TruCard from '../../components/TruCard';
import ClientList from '../../components/dashboards/lists/ClientList';
import OnboardingList from '../../components/dashboards/lists/OnboardingList';
import ResourceItem from '../../components/dashboards/items/ResourceItem';

export default function Practitioner() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [practitioner, setPractitioner] = useState({});
  const [clientList, setClientList] = useState([]);
  const { id } = useParams();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (id != null) {
      getPractitioner();
    }
  }, [id]);

  useEffect(() => {
    if (practitioner != {}) {
      getClients();
      getUnreadCount();
    }
  }, [practitioner]);

  async function getPractitioner() {
    try {
      const docRef = doc(db, "practitioners", id);
      const docSnap = await getDoc(docRef);
      setPractitioner(docSnap.data() || {});
    } catch (err) {
      console.log("Failed to get practitioner: " + err.message);
    }
  }

  async function getClients() {
    try {
      const docRef = doc(db, "practitioners", id, "clients", "list");
      const docSnap = await getDoc(docRef);
      const data = docSnap.data();
      setClientList(data.clients || []);
    } catch (err) {
      console.log(err.message);
    }
  }

  function isOnboardingComplete(practitioner) {
    if (!practitioner.onboarding || !practitioner.completedOnboarding) {
      return false;
    }
    const { training, form, cardOnFile, scheduleCall, completeCall, returnKit, personalScanReview } = practitioner.onboarding;
    return (
      !!training?.dateCompleted &&
      !!form?.dateCompleted &&
      !!cardOnFile?.dateCompleted &&
      !!scheduleCall?.dateScheduled &&
      !!completeCall?.dateCompleted &&
      !!returnKit?.dateCompleted &&
      !!personalScanReview?.dateCompleted
    );
  }

  async function getUnreadCount() {
    try {
      const now = new Date();
      const currentYear = now.getFullYear().toString();
      let unread = 0;

      // Fetch current year
      const currentRef = doc(db, "practitioners", id, "inbox", currentYear);
      const currentSnap = await getDoc(currentRef);
      if (currentSnap.exists()) {
        const threads = currentSnap.data().threads || [];
        unread += threads.filter(t => t.practitionerUnread).length;
      }

      // Fetch prior year if current month is January to June
      if (now.getMonth() < 6) {
        const priorYear = (now.getFullYear() - 1).toString();
        const priorRef = doc(db, "practitioners", id, "inbox", priorYear);
        const priorSnap = await getDoc(priorRef);
        if (priorSnap.exists()) {
          const threads = priorSnap.data().threads || [];
          unread += threads.filter(t => t.practitionerUnread).length;
        }
      }

      setUnreadCount(unread);
    } catch (err) {
      console.error("Error fetching unread count:", err.message);
    }
  }

  if (!practitioner) {
    return (
      <div style={theme.Container.basic}>
        <div style={theme.Heading}>Practitioner</div>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div style={theme.Container.flexColumnContainer}>
      <div style={theme.Container.flexColumn}>
        <Heading
          title={`${practitioner.firstName || ''} ${practitioner.lastName || ''}`}
          subTitle={practitioner.businessName ? practitioner.businessName : null}
          onClick={() => navigate(`/dashboard`)} 
        />
      </div>
      <div style={theme.Container.fullMax}>
        <div style={theme.Container.flexOne}>
          <ClientList practitionerId={id} clientList={clientList} onboardingComplete={practitioner && practitioner.completedOnboarding} cardOnFile={practitioner && practitioner.onboarding && practitioner.onboarding.cardOnFile && practitioner.onboarding.cardOnFile.dateCompleted} />
        </div>
        <div style={theme.Container.flexTwo}>
          <Heading
            title={`Chart`}
            subHeading={true}
            icon={'edit'}
            iconAction={() => navigate(`/practitioners/${id}/edit`)}
          />
          <div style={{ ...theme.Container.flexRow, width: '100%', gap: 20 }}>
            <div style={{ ...theme.Container.flexColumn, flex: 1, gap: 20 }}>
              <TruCard title={'Email'} style={{ width: '100%' }}>
                <div style={theme.TruCard.SubTextLeft}>{practitioner.email || ''}</div>
              </TruCard>
              <TruCard title={'Phone'} style={{ width: '100%' }}>
                <div style={theme.TruCard.SubTextLeft}>{practitioner.phone || ''}</div>
              </TruCard>
              <TruCard title={'General Notes'} style={{ width: '100%' }}>
                {practitioner.completedOnboarding && practitioner.onboarding?.form?.dateCompleted && 
                  <div style={{ marginBottom: 10 }}>
                    <ResourceItem
                      icon="report"
                      label="Onboarding Form"
                      actionLink={() => navigate(`/practitioners/${id}/onboarding`)}
                    />
                  </div>
                }
                <div style={theme.TruCard.SubTextLeft}>
                  <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>General Notes:</div>
                  <div style={{ marginBottom: 20 }}>{practitioner.generalNotes || 'None provided'}</div>
                  <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Supplement Lines Added:</div>
                  <div>{practitioner.supplementLines || 'None provided'}</div>
                </div>
              </TruCard>
            </div>
            <div style={{ ...theme.Container.flexColumn, flex: 1, gap: 20 }}>
              <TruCard 
                title={'Inbox'} 
                onClick={() => navigate(`/practitioners/${id}/inbox`)}
              >
                <div style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100px' }}>
                  <div style={{ fontSize: '48px', fontWeight: 'bold' }}>{unreadCount}</div>
                  <div style={{ fontSize: '14px', color: 'gray' }}>Unread Messages</div>
                </div>
              </TruCard>
              {!isOnboardingComplete(practitioner) && (
                <OnboardingList practitionerId={id} onboarding={practitioner.onboarding ? practitioner.onboarding : {}} currentUser={currentUser} setPractitioner={setPractitioner} completedOnboarding={practitioner.completedOnboarding} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}